var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-plain-input.ts
import { isIos, isSafari } from "@adornis/base/client/frontend-helpers/index.js";
import { injectAttributesOf } from "@adornis/chemistry/directives/inject-attributes.js";
import { inputResetStyle as realInputResetStyle } from "@adornis/chemistry/elements/theming/input-token.js";
import { baseConfig } from "@adornis/config/baseConfig.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { ref } from "lit/directives/ref.js";
import { DateTime } from "luxon";
import { fromEvent } from "rxjs";
import { takeUntil } from "rxjs/operators";
var cleanNumber = (num, step) => {
  const fractionalLength = (step + "").split(".")[1]?.length ?? 0;
  return num.toFixed(fractionalLength);
};
function numberToPlainString(num) {
  return ("" + +num).replace(/(-?)(\d*)\.?(\d*)e([+-]\d+)/, function(a, b, c, d, e) {
    return e < 0 ? b + "0." + Array(1 - e - c.length).join("0") + c + d : b + c + d + Array(e - d.length + 1).join("0");
  });
}
var XPlainInput = class extends FormField {
  constructor() {
    super(...arguments);
    this.type = "text";
    this.focused = false;
    this._rawValue = "";
    this.currentProcessingMode = void 0;
  }
  styles() {
    return [realInputResetStyle(this)];
  }
  render() {
    if (baseConfig.get("DEPLOYMENT") === "development" && isSafari() && ["month", "week"].includes(this.type)) {
      console.warn(
        `Using input type ${this.type} on safari. Safari does not support this input type, so it will break.`
      );
    }
    if (this.type === "search")
      return html` <form action="." @submit="${(e) => e.preventDefault()}">${this.input()}</form> `;
    return this.input();
  }
  processNewValue(value, from) {
    value ??= this._input.value;
    this.currentProcessingMode = from;
    this._rawValue = this._input.value;
    let adjustedValue = value;
    switch (this.type) {
      case "number":
        adjustedValue = this._handleNumberInput(value);
        break;
      case "iban":
        adjustedValue = this._handleIbanInput(value);
        break;
      case "tel":
        adjustedValue = this._handleTelInput(value);
        break;
      case "time":
        adjustedValue = this._handleTimeInput(value);
        break;
      case "date":
        adjustedValue = this._handleDateInput(value);
        break;
      case "datetime-local":
        adjustedValue = this._handleDateTimeLocalInput(value);
        break;
      case "week":
        adjustedValue = this._handleWeekInput(value);
        break;
      case "month":
        adjustedValue = this._handleMonthInput(value);
        break;
      default:
        adjustedValue = this._handleTextInput(value);
        break;
    }
    this.value.next(adjustedValue);
    return adjustedValue;
  }
  _applyLimit(adjustedValue, value) {
    value ??= this._input.value;
    const limit = this.getAttribute("limit");
    if (limit && !isNaN(+limit)) {
      adjustedValue = value.length > +limit ? this.value.value : this.type === "number" ? adjustedValue : value;
    }
  }
  _handleNumberInput(value) {
    value ??= this._input.value;
    const from = this.currentProcessingMode;
    let adjustedValue = value;
    if (value === "")
      return null;
    const dots = [46, 8228, 65106, 65294].map((charCode) => String.fromCharCode(charCode));
    let rawString = value.replace(",", ".");
    dots.forEach((dot) => rawString = rawString.replace(dot, "."));
    const rawNumber = +rawString;
    if (!!from) {
      const step = +(this.getAttribute("step") || 0);
      adjustedValue = !step ? rawNumber : +cleanNumber(rawNumber, step);
      const min = +(this.getAttribute("min") || -Infinity);
      const max = +(this.getAttribute("max") || Infinity);
      if (adjustedValue > max) {
        this.dispatchEvent(new CustomEvent("hit-max"));
        adjustedValue = max;
      }
      if (adjustedValue < min) {
        this.dispatchEvent(new CustomEvent("hit-min"));
        adjustedValue = min;
      }
      this._applyLimit(adjustedValue, value);
    }
    const pad = this._getNumericAttributeSafe("pad");
    if (pad && from !== "blur")
      requestAnimationFrame(() => this.setCursor(pad));
    if (Number.isNaN(adjustedValue))
      adjustedValue = null;
    return adjustedValue;
  }
  _handleMonthInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleWeekInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleDateTimeLocalInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleDateInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleTimeInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value)
      return null;
    const step = this._getNumericAttributeSafe("step");
    adjustedValue = DateTime.fromFormat(value, step && step < 60 ? "TT" : "T");
    return adjustedValue;
  }
  _handleTelInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    if (!value.length || /^[+]*[(]{0,1}[0-9]{0,4}[)]{0,1}[-\s\.\/0-9]*$/.test(value))
      adjustedValue = value;
    else
      adjustedValue = this.value.value;
    return adjustedValue;
  }
  _handleIbanInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    const oldValue = this.value.value;
    const lastCharacter = value.slice(-1);
    if (!value.length)
      adjustedValue = value;
    else if (value.length <= 2)
      if (/[A-Za-z]/.test(lastCharacter))
        adjustedValue = value.toUpperCase();
      else
        adjustedValue = oldValue;
    else if (value.length <= 34) {
      adjustedValue = value.match(/\S{1,4}/g)?.join(" ");
    } else
      adjustedValue = oldValue;
    return adjustedValue;
  }
  _filterIBANKeys(e) {
    if (!/^[a-zA-Z0-9]+$/.test(e.key)) {
      e.preventDefault();
    }
  }
  _filterIBANPaste(e) {
    const paste = e.clipboardData?.getData("text");
    e.preventDefault();
    const value = paste?.match(/\S{1,4}/g)?.join(" ");
    this.value.next(value);
  }
  _handleTextInput(value) {
    value ??= this._input.value;
    let adjustedValue = value;
    this._applyLimit(adjustedValue, value);
    return adjustedValue;
  }
  connectedCallback() {
    super.connectedCallback();
    fromEvent(this, "click").pipe(takeUntil(this.disconnected)).subscribe(() => this._input?.focus());
  }
  input() {
    return html`
      <input
        @focus=${() => this.focused = true}
        @input=${(e) => {
      this.processNewValue();
    }}
        @search=${(e) => {
      this.processNewValue();
    }}
        @keydown=${(e) => {
      switch (this.type) {
        case "number":
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            const step_direction = e.key === "ArrowUp" ? 1 : -1;
            const step = this._getNumericAttributeSafe("step") ?? 1;
            this.processNewValue(cleanNumber(+(this.value.value ?? 0) + step * step_direction, step), "arrowKey");
          }
          break;
        case "iban":
          this._filterIBANKeys(e);
          break;
      }
    }}
        @keyup=${(e) => {
      if (e.key === "Enter")
        return this.dispatchEvent(
          new CustomEvent("submit", {
            detail: { value: this.processNewValue() },
            composed: true,
            bubbles: true
          })
        );
      if (e.key === "Escape")
        return this.dispatchEvent(new CustomEvent("escape"));
    }}
        @blur=${(e) => {
      if (this.disableBlurSubmit) {
        return;
      }
      this.focused = false;
      this.processNewValue(void 0, "blur");
      this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
    }}
        @paste=${async (e) => {
      switch (this.type) {
        case "date":
          const dateMillis = Date.parse(e.clipboardData?.getData("text") ?? "");
          if (!dateMillis || isNaN(dateMillis))
            return console.warn("pasted non-parsable string", e.clipboardData?.getData("text"));
          this.value.next(DateTime.fromMillis(dateMillis));
          break;
        case "iban":
          this._filterIBANPaste(e);
          break;
      }
    }}
        @click=${(e) => e.stopPropagation()}
        .value=${live(this._displayValue())}
        ?disabled=${this.disabled}
        type=${this.type === "number" ? "text" : this.type}
        inputmode=${ifDefined(this.type === "number" ? "decimal" : void 0)}
        ${injectAttributesOf(this, ["type", "disabled"])}
        ${ref((input) => this._input = input)}
      />
    `;
  }
  select() {
    if (isIos())
      this._input?.setSelectionRange(0, 999);
    else
      this._input?.select();
  }
  setCursor(position) {
    this._input?.setSelectionRange(position, position);
  }
  getCursor() {
    return this._input ? this._input.selectionStart : null;
  }
  focus() {
    this._input?.focus();
  }
  _getNumericAttributeSafe(attr) {
    const raw = this.getAttribute(attr);
    return raw && !isNaN(+raw) ? +raw : null;
  }
  _displayValue() {
    const value = this.value.value;
    if (value === void 0 || value === null || value === "")
      return "";
    const pad = this._getNumericAttributeSafe("pad");
    const step = this._getNumericAttributeSafe("step");
    if (this.type === "number" && pad)
      return String(value).padStart(pad, "0");
    if (this.type === "number" && !!this.currentProcessingMode && typeof value === "number")
      return numberToPlainString(value);
    if (this.type === "time")
      return value.toFormat(step && step < 60 ? "TT" : "T");
    if (this.type === "date")
      return value.toISODate();
    if (this.type === "datetime-local")
      return value.toLocal().toISO({ suppressMilliseconds: true, suppressSeconds: true, includeOffset: false });
    if (this.type === "week")
      return value.toFormat("yyyy-'W'WW");
    if (this.type === "month")
      return value.toFormat("yyyy-MM");
    return value;
  }
};
__decorateClass([
  property({ reflect: true })
], XPlainInput.prototype, "type", 2);
__decorateClass([
  property({ type: Boolean, attribute: "disable-blur-submit" })
], XPlainInput.prototype, "disableBlurSubmit", 2);
__decorateClass([
  state()
], XPlainInput.prototype, "focused", 2);
XPlainInput = __decorateClass([
  customElement("x-plain-input")
], XPlainInput);
export {
  XPlainInput
};
